import React, { useEffect, useState } from 'react';
import { Plus, Scissors, CheckCircle2, Play, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, EmptyState } from '../components/ui';
import { fmtNum, fmtMoney, fmtDate, canWrite, statusColor, SIZES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';


export default function Production() {
  const { user } = useAuth();
  const write = canWrite(user.role);
  const isCutter = user.role === 'cutter';
  const isTailor = user.role === 'tailor';
  const [jobs, setJobs] = useState([]);
  const [products, setProducts] = useState([]);
  const [tailors, setTailors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [assign, setAssign] = useState(false);
  const [cut, setCut] = useState(null);
  const [complete, setComplete] = useState(null);

  const load = () => {
    api.get('/jobs').then((r) => setJobs(r.data));
    api.get('/products').then((r) => setProducts(r.data));
    api.get('/tailors').then((r) => setTailors(r.data));
    api.get('/raw-materials').then((r) => setMaterials(r.data));
  };
  useEffect(() => { load(); }, []);

  const pname = (id) => products.find((p) => p.id === id)?.name || '—';
  const tname = (id) => tailors.find((t) => t.id === id)?.name || '—';

  return (
    <div>
      <PageHeader title="Production" subtitle="Cutting & tailoring job workflow"
        actions={write && <Button onClick={() => setAssign(true)} data-testid="assign-job-btn"><Plus size={16} /> Assign Job</Button>} />

      {write && <CutterDashboard />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {['pending', 'in_progress', 'completed'].map((st) => (
          <Card key={st} className="p-3">
            <p className="text-xs font-medium text-slate-500 uppercase">{st.replace('_', ' ')}</p>
            <p className="text-2xl font-heading font-extrabold num mt-1">{jobs.filter((j) => j.status === st).length}</p>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Job #</Th><Th>Product</Th><Th>Tailor</Th><Th>Size</Th>
                <Th right>Assigned</Th><Th right>Done</Th><Th>Cut By</Th><Th>Status</Th><Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <tr key={j.id} className="hover:bg-slate-50/60 transition-colors" data-testid="job-row">
                  <Td><span className="font-mono text-xs font-semibold">{j.jobNumber}</span><span className="block text-xs text-slate-400">{fmtDate(j.date)}</span></Td>
                  <Td className="font-medium text-slate-800">{pname(j.productId)}</Td>
                  <Td>{tname(j.assignedTailorId)}</Td>
                  <Td><Badge className="bg-slate-100 border-slate-200 text-slate-600">{j.size || 'Universal'}</Badge></Td>
                  <Td right>{fmtNum(j.quantityAssigned)}</Td>
                  <Td right>{fmtNum(j.quantityCompleted)}</Td>
                  <Td className="text-xs text-slate-500">{j.cutBy || '—'}</Td>
                  <Td><Badge className={statusColor(j.status)}>{j.status.replace('_', ' ')}</Badge></Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {(write || isCutter) && !j.issuedMaterialId && j.status !== 'completed' && (
                      <Button size="sm" variant="accent" onClick={() => setCut(j)} data-testid="cut-btn"><Scissors size={13} /> Cut & Issue</Button>
                    )}
                    {(write || isTailor) && j.status !== 'completed' && (
                      <Button size="sm" variant="success" className="ml-1" onClick={() => setComplete(j)} data-testid="complete-btn"><CheckCircle2 size={13} /> Complete</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!jobs.length && <EmptyState title="No production jobs" description="Assign a tailoring job to get started." icon={Scissors} />}
      </Card>

      {assign && <AssignModal products={products} tailors={tailors} onClose={() => setAssign(false)} onSaved={() => { setAssign(false); load(); }} />}
      {cut && <CutModal job={cut} materials={materials} onClose={() => setCut(null)} onSaved={() => { setCut(null); load(); }} />}
      {complete && <CompleteModal job={complete} onClose={() => setComplete(null)} onSaved={() => { setComplete(null); load(); }} />}
    </div>
  );
}

function AssignModal({ products, tailors, onClose, onSaved }) {
  const [f, setF] = useState({ productId: products[0]?.id || '', assignedTailorId: tailors[0]?.id || '', quantityAssigned: 12, size: 'Universal' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post('/jobs', { ...f, quantityAssigned: Number(f.quantityAssigned) });
      toast.success('Job assigned'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title="Assign Tailoring Job" onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-job-btn">Assign</Button></>}>
      <div className="space-y-4">
        <Select label="Product" value={f.productId} onChange={(e) => set('productId', e.target.value)} data-testid="job-product">
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select label="Tailor" value={f.assignedTailorId} onChange={(e) => set('assignedTailorId', e.target.value)} data-testid="job-tailor">
          {tailors.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Quantity (pieces)" type="number" value={f.quantityAssigned} onChange={(e) => set('quantityAssigned', e.target.value)} data-testid="job-qty" />
          <Select label="Size" value={f.size} onChange={(e) => set('size', e.target.value)}>
            {SIZES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </div>
      </div>
    </Modal>
  );
}

function CutModal({ job, materials, onClose, onSaved }) {
  const [materialId, setMaterialId] = useState(materials[0]?.id || '');
  const [quantity, setQuantity] = useState(0);
  const save = async () => {
    try {
      await api.post(`/jobs/${job.id}/cut`, { materialId, quantity: Number(quantity) });
      toast.success('Fabric issued · job in progress'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Cut & Issue Fabric · ${job.jobNumber}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="accent" onClick={save} data-testid="save-cut-btn">Issue Fabric</Button></>}>
      <p className="text-sm text-slate-500 mb-4">Fabric is deducted from raw stock immediately upon cutting and allocated to this job.</p>
      <div className="space-y-4">
        <Select label="Fabric / Material" value={materialId} onChange={(e) => setMaterialId(e.target.value)} data-testid="cut-material">
          {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.currentStock} {m.unit})</option>)}
        </Select>
        <Input label="Quantity to cut" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="cut-qty" />
      </div>
    </Modal>
  );
}

function CompleteModal({ job, onClose, onSaved }) {
  const [qty, setQty] = useState(job.quantityAssigned);
  const save = async () => {
    try {
      await api.put(`/jobs/${job.id}/status`, { status: 'completed', quantityCompleted: Number(qty) });
      toast.success('Job completed · finished goods added'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Complete Job · ${job.jobNumber}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="success" onClick={save} data-testid="save-complete-btn">Mark Complete</Button></>}>
      <p className="text-sm text-slate-500 mb-4">Completed pieces are added to finished goods (size {job.size || 'Universal'}). {job.issuedMaterialId ? 'Materials were pre-cut, so no extra deduction.' : 'Recipe materials will be deducted.'}</p>
      <Input label="Quantity completed" type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="complete-qty" />
    </Modal>
  );
}

function CutterDashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 7)}-01`;
  const [range, setRange] = useState({ from: monthStart, to: today });
  const [mode, setMode] = useState('month');
  const [data, setData] = useState(null);

  const fetchData = (from, to) => {
    const params = [];
    if (from) params.push(`from_date=${from}`);
    if (to) params.push(`to_date=${to}`);
    api.get(`/dashboard/cutters${params.length ? `?${params.join('&')}` : ''}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { fetchData(monthStart, today); }, []);

  const quick = (m) => {
    setMode(m);
    if (m === 'today') { setRange({ from: today, to: today }); fetchData(today, today); }
    if (m === 'month') { setRange({ from: monthStart, to: today }); fetchData(monthStart, today); }
    if (m === 'lifetime') { setRange({ from: '', to: '' }); fetchData(null, null); }
  };

  return (
    <Card className="overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-end justify-between gap-3">
        <span className="text-sm font-heading font-bold text-slate-700 flex items-center gap-2"><BarChart3 size={15} className="text-[#1E3A8A]" /> Cutter Dashboard — Rolls Cut</span>
        <div className="flex flex-wrap items-end gap-2">
          {[['today', 'Today'], ['month', 'This Month'], ['lifetime', 'Lifetime']].map(([k, label]) => (
            <Button key={k} size="sm" variant={mode === k ? 'primary' : 'outline'} onClick={() => quick(k)} data-testid={`cutterdash-${k}-btn`}>{label}</Button>
          ))}
          <Input label="From" type="date" value={range.from} onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))} data-testid="cutterdash-from" />
          <Input label="To" type="date" value={range.to} onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))} data-testid="cutterdash-to" />
          <Button size="sm" onClick={() => { setMode('custom'); fetchData(range.from, range.to); }} data-testid="cutterdash-apply-btn">Apply</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr><Th>Cutter</Th><Th right>Rolls (Period)</Th><Th right>Earnings (Period)</Th><Th right>Rolls (Lifetime)</Th><Th right>Earnings (Lifetime)</Th><Th right>Rate/Roll</Th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.rows || []).map((c) => (
              <tr key={c.userId} data-testid="cutterdash-row" className="hover:bg-slate-50/60">
                <Td className="font-medium text-slate-800">{c.name}</Td>
                <Td right className="num font-semibold">{fmtNum(c.periodRolls)}</Td>
                <Td right className="num">{fmtMoney(c.periodEarnings)}</Td>
                <Td right className="num">{fmtNum(c.lifetimeRolls)}</Td>
                <Td right className="num">{fmtMoney(c.lifetimeEarnings)}</Td>
                <Td right className="num text-slate-500">{fmtMoney(c.rollRate)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data?.rows?.length && <EmptyState title="No cutters" icon={Scissors} />}
    </Card>
  );
}
